import { Card, CardActionArea, CardContent } from '@material-ui/core';
import { Color, Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { PostingPrivateTag } from 'shared/modules/loadboard/PostingPrivateTag';
import { WebViewAnchorButton } from 'shared/routing/WebViewLinks';
import styled from 'styled-components';
import { trackDriverOpenedPostingIfSuggested } from '../../core/DriverSuggestedLoadAnalytics';
import {
  DriverSuggestedLoadOpenSource,
  trackLoadboardEvent,
} from '../../core/LoadboardAnalytics';
import { PostingLoadEventObserver } from '../../core/PostingLoadEventObserver';
import { CarrierPostingStatusInfo } from '../../core/postings/CarrierPostingStatusInfo';
import { EditedByShipperTag } from '../../core/postings/EditedByShipperTag';
import { PostingAvailableDate } from '../../core/postings/PostingAvailableDate';
import { PostingEditedLabel } from '../../core/postings/PostingEditedLabel';
import { PostingHiddenShipperTooltip } from '../../core/postings/PostingHiddenShipperTooltip';
import { PostingVehicle } from '../../core/postings/PostingVehicle';
import { PostingVehiclesSummary } from '../../core/postings/PostingVehiclesSummary';
import { PostingWaypoints } from '../../core/postings/PostingWaypoints';
import { ShipperCompanyName } from '../../core/postings/ShipperCompanyName';
import { SavedLoadStatus } from '../../core/saved-loads/SavedLoadStatus';
import { UnpostedSavedLoadOverlay } from '../../core/saved-loads/UnpostedSavedLoadOverlay';
import { useLoadboardContext } from '../../data/LoadboardContext';
import {
  PostingDTO,
  getPostingState,
  getViewOfferLink,
} from '../../data/dto/PostingDTO';
import { PostingPaymentDetails } from '../PostingPaymentDetails';
import { PostingPriceLabel } from '../PostingPriceLabel';
import { useLoadboardAccess } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessAPI';
import { useFlag } from 'shared/settings/FeatureToggles';

const StyledCard = styled(Card)<{ $highlighted?: boolean }>`
  position: relative;
  transition: border-color 0.2s ease-in-out;

  ${({ $highlighted }) =>
    $highlighted &&
    `&::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: 4px;
      background-color: ${Color.Blue300};
    }
  `}
`;

export interface PostingListsCardProps {
  posting: PostingDTO;
  source?: string;
  suggestedLoadOpenSource?: DriverSuggestedLoadOpenSource;
}

export function PostingListCard({
  posting,
  source,
  suggestedLoadOpenSource,
}: PostingListsCardProps) {
  const {
    load,
    carrier_load: carrierPostingInfo,
    pickup_distance,
    delivery_distance,
  } = posting;

  const {
    router: { openPostingScreen },
  } = useLoadboardContext();
  const { data: access } = useLoadboardAccess();
  const isEditedByShipperFlagEnabled = useFlag('slb_edited_by_shipper');

  const postingStatus = getPostingState(load, carrierPostingInfo);
  const hasEditedByShipperTag =
    isEditedByShipperFlagEnabled && !!carrierPostingInfo?.load_edited_at;

  const viewOfferLink =
    carrierPostingInfo && getViewOfferLink(carrierPostingInfo, 'Load List');

  return (
    <PostingLoadEventObserver posting={posting}>
      {(ref) => (
        <StyledCard
          ref={ref}
          role="button"
          data-cy="LoadCard"
          data-testid="LoadCard"
          $highlighted={!!posting.is_saved}
          data-cy-saved={posting.is_saved}
          onClick={() => {
            if (access?.reported) {
              return;
            }

            trackDriverOpenedPostingIfSuggested(
              posting,
              suggestedLoadOpenSource,
            );
            openPostingScreen(posting, 'push', source);
          }}
        >
          <UnpostedSavedLoadOverlay posting={posting} />

          <CardActionArea>
            <CardContent>
              <Stack space="small">
                <Stack space="xsmall">
                  <Columns>
                    <Column>
                      <Stack space="xsmall">
                        {carrierPostingInfo &&
                          (hasEditedByShipperTag ? (
                            <Inline>
                              <CarrierPostingStatusInfo
                                carrierPostingInfo={carrierPostingInfo}
                              />
                              <EditedByShipperTag />
                            </Inline>
                          ) : (
                            <CarrierPostingStatusInfo
                              carrierPostingInfo={carrierPostingInfo}
                            />
                          ))}

                        <Inline space="small">
                          <PostingPriceLabel
                            load={load}
                            isBookable={postingStatus === 'can_book'}
                          />

                          {load.is_posted_to_private_loadboard && (
                            <PostingPrivateTag />
                          )}
                        </Inline>
                      </Stack>
                    </Column>

                    <Column width="content">
                      <SavedLoadStatus size="medium" posting={posting} />
                    </Column>
                  </Columns>

                  <PostingPaymentDetails load={load} source="Load List" />

                  <PostingWaypoints
                    load={load}
                    pickupDistance={pickup_distance}
                    deliveryDistance={delivery_distance}
                  />

                  {load.vehicles.length > 0 &&
                    (load.vehicles.length === 1 && load.vehicles[0] ? (
                      <PostingVehicle
                        vehicle={load.vehicles[0]}
                        transportType={load.transport_type}
                        hideVehicleInfo={!!load.hide_vehicle_info}
                      />
                    ) : (
                      <PostingVehiclesSummary
                        vehicles={load.vehicles}
                        transportType={load.transport_type}
                        title={`${load.vehicles.length} Vehicles`}
                        hideVehicleInfo={!!load.hide_vehicle_info}
                      />
                    ))}

                  {load.shipper ? (
                    <ShipperCompanyName shipper={load.shipper} />
                  ) : (
                    <PostingHiddenShipperTooltip />
                  )}

                  <PostingAvailableDate {...load} />

                  {!hasEditedByShipperTag &&
                    carrierPostingInfo?.load_edited_at && (
                      <PostingEditedLabel
                        loadEditedAt={carrierPostingInfo.load_edited_at}
                        variant="list"
                      />
                    )}
                </Stack>
                {postingStatus === 'received_offer' && viewOfferLink && (
                  <WebViewAnchorButton
                    fullWidth={true}
                    variant="neutral"
                    target="_blank"
                    href={viewOfferLink}
                    onClick={(event) => {
                      event.stopPropagation();
                      trackLoadboardEvent({
                        medium: 'list',
                        name: 'SLB: Clicked View Load Offer',
                        type:
                          postingStatus === 'received_offer'
                            ? 'offer'
                            : 'counter_offer',
                      });
                    }}
                  >
                    View Offer
                  </WebViewAnchorButton>
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </StyledCard>
      )}
    </PostingLoadEventObserver>
  );
}
